import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = process.env.REACT_APP_API_GATEWAY_URL || 'https://diuh85h4d5.execute-api.us-east-1.amazonaws.com/prod';

export interface SupportMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface StartConversationResponse {
  conversationId: string;
  sessionToken: string;
  status: string;
  messages: SupportMessage[];
}

export interface SendMessageResponse {
  message: SupportMessage;
  messageCount: number;
}

export interface GetConversationResponse {
  conversationId: string;
  status: string;
  messageCount: number;
  messages: SupportMessage[];
}

export interface EscalateResponse {
  status: string;
  message: string;
}

interface SupportApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
  meta?: { requestId: string };
}

async function supportApiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  sessionToken?: string
): Promise<SupportApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) {
      headers['Authorization'] = token;
    }
  } catch {
    // Not authenticated
  }

  if (sessionToken) {
    headers['x-session-token'] = sessionToken;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  const data = await response.json();
  return data as SupportApiResponse<T>;
}

export const supportApi = {
  startConversation: (email?: string, sessionToken?: string) =>
    supportApiRequest<StartConversationResponse>('/api/support/conversations', {
      method: 'POST',
      body: JSON.stringify({ email, sessionToken }),
    }, sessionToken),

  sendMessage: (conversationId: string, message: string, sessionToken?: string) =>
    supportApiRequest<SendMessageResponse>(`/api/support/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message, sessionToken }),
    }, sessionToken),

  getConversation: (conversationId: string, sessionToken?: string) =>
    supportApiRequest<GetConversationResponse>(`/api/support/conversations/${conversationId}?sessionToken=${sessionToken || ''}`, {
      method: 'GET',
    }, sessionToken),

  escalate: (conversationId: string, reason?: string, sessionToken?: string) =>
    supportApiRequest<EscalateResponse>(`/api/support/conversations/${conversationId}/escalate`, {
      method: 'POST',
      body: JSON.stringify({ reason, sessionToken }),
    }, sessionToken),

  resolve: (conversationId: string, sessionToken?: string) =>
    supportApiRequest<{ status: string }>(`/api/support/conversations/${conversationId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ sessionToken }),
    }, sessionToken),
};
